/**
 * Same-origin messaging between tabs of one app.
 *
 * Transport, in order of preference:
 *
 *   1. `BroadcastChannel` — preferred because it keeps working when storage is
 *      blocked (Safari private mode, "block all cookies", enterprise policy),
 *      which is exactly the case where a storage-only channel would die
 *      silently. It also writes nothing to disk.
 *   2. The `storage` event — covers browsers without BroadcastChannel.
 *   3. Nothing. Every method becomes a no-op and each tab simply behaves as it
 *      did before this module existed, rather than throwing.
 *
 * A sender never receives its own message: BroadcastChannel does not deliver to
 * the posting context, `storage` does not fire in the writing tab, and the
 * `senderId` guard covers anything that slips through. Callers therefore apply
 * their own change locally and post afterwards.
 */

/** Identifies this tab for the lifetime of the document. */
export const TAB_ID: string = (() => {
  try {
    const uuid = (window.crypto as any)?.randomUUID?.();
    if (uuid) return uuid as string;
  } catch {
    // fall through to the manual id below
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
})();

export type CrossTabTransport = 'broadcast' | 'storage' | 'none';

interface CrossTabEnvelope<M> {
  v: number;
  /** Identity the message belongs to; a mismatch is dropped on receipt. */
  scope: string;
  senderId: string;
  sentAt: number;
  /**
   * Forces every write to differ. The `storage` event does not fire when the
   * stored value is unchanged, so two identical messages in a row would be
   * delivered once without this.
   */
  msgId: string;
  message: M;
}

export interface CrossTabChannel<M> {
  post(message: M): void;
  subscribe(handler: (message: M) => void): () => void;
  close(): void;
  readonly transport: CrossTabTransport;
}

const NOOP_UNSUBSCRIBE = () => undefined;

const isStorageWritable = (): boolean => {
  try {
    const probe = '__mwv_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

const createEnvelope = <M>(
  message: M,
  scope: string,
  version: number
): CrossTabEnvelope<M> => ({
  v: version,
  scope,
  senderId: TAB_ID,
  sentAt: Date.now(),
  msgId: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  message,
});

const isDeliverable = <M>(
  envelope: CrossTabEnvelope<M> | null,
  scope: string,
  version: number
): envelope is CrossTabEnvelope<M> =>
  !!envelope &&
  envelope.v === version &&
  envelope.scope === scope &&
  envelope.senderId !== TAB_ID;

export const createCrossTabChannel = <M>(
  name: string,
  scope: string,
  version: number
): CrossTabChannel<M> => {
  // ---------------------------------------------------------------- broadcast
  try {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel(name);

      return {
        transport: 'broadcast',
        post: (message) => {
          try {
            channel.postMessage(createEnvelope(message, scope, version));
          } catch (error) {
            console.warn('[video-notifications] broadcast post failed', error);
          }
        },
        subscribe: (handler) => {
          const listener = (event: MessageEvent) => {
            const envelope = event?.data as CrossTabEnvelope<M> | null;
            if (isDeliverable(envelope, scope, version)) handler(envelope.message);
          };
          channel.addEventListener('message', listener);
          return () => channel.removeEventListener('message', listener);
        },
        close: () => {
          try {
            channel.close();
          } catch {
            // already closed — nothing to do
          }
        },
      };
    }
  } catch (error) {
    console.warn('[video-notifications] BroadcastChannel unavailable', error);
  }

  // ------------------------------------------------------------------ storage
  if (typeof window !== 'undefined' && isStorageWritable()) {
    const storageKey = `${name}:signal`;

    return {
      transport: 'storage',
      post: (message) => {
        try {
          window.localStorage.setItem(
            storageKey,
            JSON.stringify(createEnvelope(message, scope, version))
          );
        } catch (error) {
          console.warn('[video-notifications] storage post failed', error);
        }
      },
      subscribe: (handler) => {
        const listener = (event: StorageEvent) => {
          if (event.key !== storageKey || !event.newValue) return;
          let envelope: CrossTabEnvelope<M> | null = null;
          try {
            envelope = JSON.parse(event.newValue);
          } catch {
            return;
          }
          if (isDeliverable(envelope, scope, version)) handler(envelope.message);
        };
        window.addEventListener('storage', listener);
        return () => window.removeEventListener('storage', listener);
      },
      close: () => undefined,
    };
  }

  // --------------------------------------------------------------------- none
  return {
    transport: 'none',
    post: () => undefined,
    subscribe: () => NOOP_UNSUBSCRIBE,
    close: () => undefined,
  };
};
